import {
  Backdrop,
  Box,
  Button,
  DialogContent,
  IconButton,
  Dialog as MuiDialog,
  Typography,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import React, { FC } from "react";

interface GenericModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
  message?: string;
  messageSubtitle?: string;
  width?: string | number;
  fontFamily?: string;
  cancelText?: string;
  confirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const Dialog: FC<GenericModalProps> = ({
  open,
  onClose,
  title = "",
  children,
  width = 394,
  fontFamily = "Poppins",
  cancelText = "Cancelar",
  confirmText = "Aceptar",
  message = "¿Estás seguro de realizar esta acción? ",
  messageSubtitle = "", 
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const totalButtons = [onCancel, onConfirm].filter(Boolean).length;
  
  const getButtonStyle = () => {
    if (totalButtons === 1) {
      return {
        width: "100px",
        justifyContent: "center",
      };
    } else {
      return {
        flex: 1,
      };
    }
  };
  
  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "white",
          padding: 0,
          width: { xs: "90vw", sm: width },
          boxShadow: "none",
          borderRadius: 2,
        },
      }}
    >
      <DialogContent sx={{ p: "18px 21px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 0,
            m: 0,
            gap: "21px",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            p={0}
          >
            <Typography
              sx={{
                fontFamily: fontFamily,
                fontWeight: 500,
                textAlign: "start",
                flexGrow: 1,
                color: "text.secondary",
                fontSize: "15px",
              }}
            >
              {title}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                padding: "4px",
                backgroundColor: "text.disabled",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "text.disabled",
                },
              }}
            >
              <Close 
                sx={{ 
                  color: theme.palette.background.paper,
                  fontSize: "18px"
                }} 
              />
            </IconButton>
          </Box>
          {children ? (
            <Box width="100%">{children}</Box>
          ) : (
            <Box width="100%">
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  color: "text.secondary",
                  fontSize: { xs: "11px", sm: "12px" },
                  mb: 1,
                }}
              >
                {message}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  color: "error.main",
                  fontSize: { xs: "10px", sm: "11px" },
                }}
              >
                {messageSubtitle}
              </Typography>
            </Box>
          )}
        </Box>
        {(onCancel || onConfirm) && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 2,
              justifyContent: totalButtons === 1 ? "center" : "space-between",
              mt: 2,
            }}
          >
            {onConfirm && (
              <Button
                variant="contained"
                onClick={onConfirm}
                sx={{
                  ...getButtonStyle(),
                  width: totalButtons === 1 ? "100%" : "50%",
                  backgroundColor: "error.main",
                  "&:hover": {
                    backgroundColor: "error.dark",
                  },
                }}
              >
                {confirmText}
              </Button>
            )}
            {onCancel && (
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  ...getButtonStyle(),
                  backgroundColor: "text.disabled",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "text.disabled",
                  },
                }}
              >
                {cancelText}
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
    </MuiDialog>
  );
};

export default Dialog;
